import type { DisplayMessage } from '../context/use-chat-context';
import type { UnifiedSearchApiResponse, Store } from '../services/types';
import { ResponseType } from '../services/types';
import { Featureflag } from '../constants/featureFlags';
import { PERSISTENT_MESSAGE_TIMEOUT } from '../constants/common';

export function isAiFiltersEnabledInStore(store: Store | undefined): boolean {
  return store?.config?.config.moduleEnabled?.includes('ai-filters') ?? false;
}

export function isVoiceResponsesEnabledInStore(
  store: Store | undefined
): boolean {
  return (
    store?.config?.config.moduleEnabled?.includes('voice-responses') ?? false
  );
}

export function buildEnabledFlags({
  isAIFiltersEnabled,
  isCollectionsEnabled,
}: {
  isAIFiltersEnabled: boolean;
  isCollectionsEnabled: boolean;
}): Featureflag[] {
  const flags: Featureflag[] = [];
  if (isAIFiltersEnabled) flags.push(Featureflag.AI_FILTERS);
  if (isCollectionsEnabled) flags.push(Featureflag.COLLECTIONS);
  return flags;
}

export function shouldShowAiFilters({
  isAIFiltersEnabled,
  searchId,
  productsCount,
  appliedFiltersCount,
}: {
  isAIFiltersEnabled: boolean;
  searchId: string | null;
  productsCount?: number;
  appliedFiltersCount: number;
}): boolean {
  const hasSearchId = Boolean(searchId);
  const hasSufficientResults = (productsCount ?? 0) >= 8;
  const hasAppliedFilters = appliedFiltersCount > 0;

  return (
    isAIFiltersEnabled &&
    hasSearchId &&
    (hasSufficientResults || hasAppliedFilters)
  );
}

export function getMessageToDisplay({
  response,
  isAIFiltersEnabled,
}: {
  response: UnifiedSearchApiResponse;
  isAIFiltersEnabled: boolean;
}): string {
  if (isAIFiltersEnabled) {
    return response.answer;
  }

  return response.responseType === ResponseType.PRODUCT_SEARCH
    ? ''
    : response.answer;
}

export function hasCodeBlock(message: string): boolean {
  return /```[\s\S]*?```/.test(message);
}

export function createDisplayMessage({
  response,
  isAIFiltersEnabled,
}: {
  response: UnifiedSearchApiResponse;
  isAIFiltersEnabled: boolean;
}): DisplayMessage {
  const messageText = getMessageToDisplay({ response, isAIFiltersEnabled });
  const hasCode = hasCodeBlock(messageText);

  return {
    userQuery: response.query,
    systemAnswer: messageText,
    suggestedCTA: response.suggestedCTA,
    hasCode,
    autoHideTimeout: response.autoHideTimeout,
    citations: response.citations,
  };
}

/**
 * Creates a fallback onboarding message when API call fails
 * @returns DisplayMessage with default welcome text
 */
export function createFallbackOnboardingMessage(): DisplayMessage {
  return {
    userQuery: '',
    systemAnswer:
      "Hey, I'm Superbar. I'm here to help you explore our products. Let's get started!",
    autoHideTimeout: PERSISTENT_MESSAGE_TIMEOUT,
  };
}

/**
 * Creates a display message from onboarding API response
 * @param response - UnifiedSearchApiResponse from onboarding API
 * @returns DisplayMessage formatted for UI display
 */
export function createOnboardingDisplayMessage(
  response: UnifiedSearchApiResponse
): DisplayMessage {
  return {
    userQuery: '',
    systemAnswer: response.answer,
    suggestedCTA: response.suggestedCTA,
    autoHideTimeout: response.autoHideTimeout ?? PERSISTENT_MESSAGE_TIMEOUT,
  };
}
